import type { Bill } from "../../lib/api-modules/types.js";
import { costPerUse } from "../../lib/util/cost-per-use.js";
import { formatCentsCurrency } from "../../lib/util/money.js";
import { Card } from "../design-system/index.js";

/**
 * Cost-per-use estimator tile. Phase 5 Session 6.
 *
 * Renders inline below subscription bills (recurrence in monthly /
 * quarterly / annual). Surfaces $/use with "estimated based on N
 * uses/month — adjust above" copy when the user hasn't supplied an
 * explicit `expected_uses_per_month`. Returns null for one_time bills.
 */
interface CostPerUseTileProps {
  readonly bill: Bill;
}

export function CostPerUseTile({ bill }: CostPerUseTileProps) {
  const input: {
    amount_cents: number;
    recurrence: Bill["recurrence"];
    expected_uses_per_month?: number;
    category?: string;
  } = {
    amount_cents: bill.amount_cents,
    recurrence: bill.recurrence,
  };
  if (bill.expected_uses_per_month !== undefined) {
    input.expected_uses_per_month = bill.expected_uses_per_month;
  }
  if (bill.category !== undefined) {
    input.category = bill.category;
  }
  const result = costPerUse(input);
  if (!result) return null;

  return (
    <Card padded={false} className="mt-3 grid gap-3 p-4 md:grid-cols-3">
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
          Cost / use
        </p>
        <p className="mt-1 font-mono text-lg font-medium tabular-nums text-[var(--color-text)]">
          {formatCentsCurrency(result.cost_per_use_cents)}
        </p>
      </div>
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
          Per month
        </p>
        <p className="mt-1 font-mono text-lg font-medium tabular-nums text-[var(--color-text)]">
          {formatCentsCurrency(result.monthly_cost_cents)}
        </p>
      </div>
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
          Uses / month
        </p>
        <p className="mt-1 font-mono text-lg font-medium tabular-nums text-[var(--color-text)]">
          {result.uses_per_month}
          {result.used_default && (
            <span className="ml-1 text-xs font-normal text-[var(--color-faint)]">
              (estimated)
            </span>
          )}
        </p>
      </div>
      {result.used_default && (
        <p className="text-xs text-[var(--color-muted)] md:col-span-3">
          Estimated based on {result.uses_per_month} use
          {result.uses_per_month === 1 ? "" : "s"}/month
          {bill.category ? ` for "${bill.category}"` : ""}. Edit the bill to
          override.
        </p>
      )}
    </Card>
  );
}
